import { useCallback, useRef } from 'react';

interface UseDraggableProps {
  selectedIndex: number;
  itemsCount: number;
  containerRef: React.RefObject<HTMLDivElement>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  recalcOffset: () => void;
  gap?: number;
  thresholdRatio?: number;
}

/**
 * 카드 트랙 드래그 훅 – 마우스·터치 공통 지원
 * 1. 드래그 중 트랙 transition 을 끄고 바로 따라오게 함 (데스크톱 버벅임 해결)
 * 2. 카드 폭 * ⅓ 이상 이동 시 인덱스 ±1, 아니면 제자리 스냅
 * 3. 트랙 offset 은 min / max 범위를 넘어가지 않도록 클램프
 */
export default function useDraggable({
  selectedIndex,
  itemsCount,
  containerRef,
  offset,
  setOffset,
  setSelectedIndex,
  recalcOffset,
  gap = 32,
  thresholdRatio = 1 / 3,
}: UseDraggableProps) {
  const startXRef = useRef(0);
  const startOffsetRef = useRef(0);
  const isDraggingRef = useRef(false);
  const prevTransitionRef = useRef<string | null>(null);

  const getCardWidth = () => {
    const el = containerRef.current?.querySelector<HTMLDivElement>('.card');
    return el ? el.offsetWidth + gap : 0;
  };

  const getClampOffset = (raw: number) => {
    const wrap = containerRef.current;
    const cardWidth = getCardWidth();
    if (!wrap || cardWidth === 0) return raw;

    const centerSpace = (wrap.offsetWidth - (cardWidth - gap)) / 2;
    const min = centerSpace - (itemsCount - 1) * cardWidth; // 가장 마지막 카드
    const max = centerSpace; // 첫 카드
    return Math.min(Math.max(raw, min), max);
  };

  const enableNoTransition = () => {
    const track = containerRef.current?.firstElementChild as HTMLElement | null;
    if (!track) return;
    prevTransitionRef.current = track.style.transition;
    track.style.transition = 'none';
  };

  const restoreTransition = () => {
    const track = containerRef.current?.firstElementChild as HTMLElement | null;
    if (!track) return;
    if (prevTransitionRef.current !== null) {
      track.style.transition = prevTransitionRef.current;
      prevTransitionRef.current = null;
    }
  };

  const didDrag = useRef(false); // ⭐️ 드래그 여부 기록

  const onMove = useCallback(
    (clientX: number) => {
      const deltaX = clientX - startXRef.current;
      const newOffset = getClampOffset(startOffsetRef.current + deltaX);
      if (Math.abs(deltaX) > 5) {
        didDrag.current = true;
      }
      setOffset(newOffset);
    },
    [setOffset]
  );

  const endDrag = useCallback(
    (clientX: number) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;

      const deltaTotal = clientX - startXRef.current;
      const cardWidth = getCardWidth();
      const threshold = cardWidth * thresholdRatio;

      let nextIndex = selectedIndex;
      // console.log('data ', Math.abs(deltaTotal), ' ', threshold);
      if (Math.abs(deltaTotal) > threshold) {
        const dir = deltaTotal < 0 ? 1 : -1;
        nextIndex = Math.min(Math.max(selectedIndex + dir, 0), itemsCount - 1);
      }

      restoreTransition();

      // console.log('nextIndex ', nextIndex, ' ', selectedIndex);
      if (nextIndex !== selectedIndex) {
        setSelectedIndex(nextIndex);
      } else {
        recalcOffset();
      }

      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);

      setTimeout(() => (didDrag.current = false), 0); // ⭐️ 드래그 플래그 초기화(한 프레임 뒤)
    },
    [itemsCount, recalcOffset, selectedIndex, setSelectedIndex, thresholdRatio]
  );

  // ---------------- Mouse ----------------
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startOffsetRef.current = offset;
      isDraggingRef.current = true;
      enableNoTransition();

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [offset]
  );

  const handleMouseMove = (e: MouseEvent) => onMove(e.clientX);
  const handleMouseUp = (e: MouseEvent) => endDrag(e.clientX);

  // ---------------- Touch ----------------
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      startXRef.current = touch.clientX;
      startOffsetRef.current = offset;
      isDraggingRef.current = true;
      enableNoTransition();

      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    },
    [offset]
  );

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDraggingRef.current) return;
    const touch = e.touches[0];
    onMove(touch.clientX);
    e.preventDefault(); // 세로 스크롤 방지
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const touch = e.changedTouches[0];
    endDrag(touch?.clientX ?? 0);
  };

  // 기본 이미지/링크 드래그 방지 (데스크톱 크롬 등에서 드래그 지연 방지)
  const trackEl = containerRef.current?.firstElementChild as HTMLElement | null;
  if (trackEl) trackEl.ondragstart = () => false;

  return {
    handleMouseDown,
    handleTouchStart,
    didDrag,
  };
}
